import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@repo/ui'
import { BookOpen, FilePlus2, FileText, Plus, Upload } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { sourceOriginLabel, useSourceDetails } from './SourceDetails'
import type { SourceSummaryLoader } from './SourceDetails'
import type { ChatContextSelection } from './chatContextState'

// The chat can flex below its 44rem maximum: 568px chat + 360px card + 32px spacing.
export function useChatContextRoom() {
  const ref = useRef<HTMLElement>(null)
  const [hasRoom, setHasRoom] = useState(false)
  useEffect(() => {
    const element = ref.current
    if (element === null || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => setHasRoom((entry?.contentRect.width ?? 0) >= 960))
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  return { ref, hasRoom }
}

export function ChatContextPanel({
  selection,
  pendingDocuments,
  loadSourceSummaries,
  onOpenSources,
  onOpenFacts,
  onLatest,
  onAddSources,
  onUploadSource
}: {
  selection: ChatContextSelection
  pendingDocuments: Array<{ id: number; label: string }>
  loadSourceSummaries?: SourceSummaryLoader
  onOpenSources: (messageId: number) => void
  onOpenFacts: (briefId: number) => void
  onLatest: () => void
  onAddSources?: () => void
  onUploadSource?: () => void
}) {
  const openDocumentsAfterClose = useRef(false)
  const details = useSourceDetails(selection.sourceIds.slice(0, 3), loadSourceSummaries)
  const load = details.load
  useEffect(() => load(), [load])
  const answer = selection.answer
  return (
    <div className="space-y-5 p-4 text-sm">
      <section aria-label="Sources for this answer">
        <div className="flex items-center justify-between gap-2">
          <h3 className="flex-1 text-xs font-medium text-muted-foreground">Sources for this answer</h3>
          {selection.sourceIds.length > 0 && (
            <span className="text-xs text-muted-foreground tabular-nums">{selection.sourceIds.length}</span>
          )}
          {onAddSources !== undefined && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="-my-1 size-8 shrink-0 rounded-lg text-muted-foreground"
                  aria-label="Add sources"
                >
                  <Plus className="size-4" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                aria-label="Add sources"
                align="end"
                sideOffset={6}
                collisionPadding={8}
                className="w-60 rounded-xl p-1.5"
                onCloseAutoFocus={(event) => {
                  if (!openDocumentsAfterClose.current) return
                  openDocumentsAfterClose.current = false
                  event.preventDefault()
                  onAddSources()
                }}
              >
                <DropdownMenuItem
                  className="min-h-11 rounded-lg px-3"
                  onSelect={() => {
                    openDocumentsAfterClose.current = true
                  }}
                >
                  <FilePlus2 aria-hidden="true" />
                  Attach documents
                </DropdownMenuItem>
                {onUploadSource !== undefined && (
                  <DropdownMenuItem className="min-h-11 rounded-lg px-3" onSelect={onUploadSource}>
                    <Upload aria-hidden="true" />
                    Upload a file
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{selection.isLatest ? 'Latest answer' : 'Selected answer'}</span>
          {!selection.isLatest && (
            <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={onLatest}>
              Latest answer
            </Button>
          )}
        </div>
        {selection.sourceIds.length === 0 ? (
          <p className="mt-3 text-xs leading-5 text-muted-foreground">
            {answer === undefined
              ? 'Sources will appear when an answer cites them.'
              : 'No sources were cited for this answer.'}
          </p>
        ) : details.errorMessage !== null ? (
          <div className="mt-3 space-y-2">
            <p role="alert" className="text-xs text-destructive">
              {details.errorMessage}
            </p>
            {details.retryable && (
              <Button type="button" variant="outline" size="sm" onClick={details.retry}>
                Retry source details
              </Button>
            )}
          </div>
        ) : details.summaries === null ? (
          <p role="status" className="mt-3 text-xs text-muted-foreground">
            Loading source details
          </p>
        ) : (
          <ul className="mt-2 space-y-1">
            {details.summaries.map((source) => (
              <li key={source.docId}>
                <button
                  type="button"
                  className="flex w-full items-start gap-2.5 rounded-md px-2 py-2 text-left outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => answer !== undefined && onOpenSources(answer.messageId)}
                >
                  <BookOpen className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block text-sm leading-5 break-words">
                      {source.fileName.trim() || 'Workspace source'}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{sourceOriginLabel(source)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {answer !== undefined && selection.sourceIds.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-1 text-xs text-muted-foreground"
            onClick={() => onOpenSources(answer.messageId)}
          >
            {selection.sourceIds.length > 3 ? `View all ${selection.sourceIds.length} sources` : 'View source details'}
          </Button>
        )}
      </section>
      {selection.documents.length > 0 && selection.briefId !== undefined && (
        <section aria-label="In this chat" className="border-t pt-4">
          <h3 className="text-xs font-medium text-muted-foreground">In this chat</h3>
          <p className="mt-1 text-xs text-muted-foreground">Recorded source material</p>
          <ul className="mt-2 space-y-1">
            {selection.documents.slice(0, 3).map((document) => (
              <li key={document.id}>
                <button
                  type="button"
                  className="flex w-full items-start gap-2.5 rounded-md px-2 py-2 text-left outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => onOpenFacts(selection.briefId!)}
                >
                  <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className="min-w-0 break-words">{document.label}</span>
                </button>
              </li>
            ))}
          </ul>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-1 text-xs text-muted-foreground"
            onClick={() => onOpenFacts(selection.briefId!)}
          >
            {selection.documents.length > 3
              ? `View all ${selection.documents.length} documents`
              : 'View recorded context'}
          </Button>
        </section>
      )}
      {pendingDocuments.length > 0 && (
        <section aria-label="For your next message" className="border-t pt-4">
          <h3 className="text-xs font-medium text-muted-foreground">For your next message</h3>
          <ul className="mt-2 space-y-2">
            {pendingDocuments.slice(0, 3).map((document) => (
              <li key={document.id} className="flex items-start gap-2.5 px-2 py-1">
                <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="min-w-0 break-words">{document.label}</span>
              </li>
            ))}
          </ul>
          {pendingDocuments.length > 3 && (
            <p className="mt-2 text-xs text-muted-foreground">And {pendingDocuments.length - 3} more in the composer</p>
          )}
        </section>
      )}
    </div>
  )
}
